import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { MediaCardProps } from './MediaCard.types';
import styles from './MediaCard.module.scss';

/** Default placeholder image shared by the event and map-bundle cards. */
export const DEFAULT_CARD_IMAGE = '/background_map.jpg';

/**
 * Shared card shell for grid pages: an image header with optional top-left/top-right
 * overlay slots, a truncated title, and a flex-column body for caller-supplied content.
 * Falls back to `fallbackSrc` if the image fails to load, and resets when `imageSrc`
 * changes (e.g. a bundle thumbnail becoming available after generation).
 */
export default function MediaCard({
    title,
    imageSrc = DEFAULT_CARD_IMAGE,
    imageAlt = '',
    fallbackSrc = DEFAULT_CARD_IMAGE,
    onClick,
    topLeft,
    topRight,
    children,
    className = '',
    testId,
}: MediaCardProps): React.ReactElement {
    const [src, setSrc] = useState(imageSrc);

    useEffect(() => {
        setSrc(imageSrc);
    }, [imageSrc]);

    const handleError = useCallback(() => {
        setSrc((current) => (current !== fallbackSrc ? fallbackSrc : current));
    }, [fallbackSrc]);

    return (
        <Card
            className={`${styles.card} h-100 ${onClick ? styles.clickable : ''} ${className}`}
            onClick={onClick}
            data-testid={testId}
        >
            <div className={styles.imageContainer}>
                <img
                    src={src}
                    alt={imageAlt}
                    className={styles.image}
                    onError={handleError}
                />
                {topLeft && <div className={styles.topLeft}>{topLeft}</div>}
                {topRight && <div className={styles.topRight}>{topRight}</div>}
            </div>
            <Card.Body className="d-flex flex-column">
                <Card.Title className={styles.title}>{title}</Card.Title>
                {children}
            </Card.Body>
        </Card>
    );
}
